/**
 * ForecastingPage - Load & Generation Forecasting tool.
 *
 * Route: /communities/:id/forecasting
 *
 * Two forecasting modes:
 *   1. Short-term: Day-ahead hourly forecasts (24-48 hours)
 *   2. Long-term: Monthly forecasts up to 6 months ahead
 *
 * Setup panel allows:
 *   - Selecting forecast mode (short-term / long-term)
 *   - Choosing forecast type (load / generation)
 *   - Selecting date range
 *   - Selecting source datasets
 *   - Configuring forecast parameters
 *
 * Results area shows:
 *   - Forecast charts (placeholder)
 *   - Forecast statistics table
 *   - Export options
 */

import React, { useState, useMemo } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'
import {
  Row, Col, Card, Form, Button, Badge, Alert, Spinner, Tab, Nav,
} from 'react-bootstrap'
import Chart from 'react-apexcharts'
import { useTranslation } from 'react-i18next'
import WeatherUploadForm from '../components/WeatherUploadForm'
import { czechLoadProfile, czechPVProfile, communityKPIs, getSeason, estimateBuildingAnnualKwh } from '../data/czechEnergyProfiles'
import { useShortTermForecast, useLongTermForecast } from '../hooks/useForecast'
import { forecastAPI } from '../api/forecast'
import { getValues } from '../api/datasets'
import { resultsAPI } from '../api/results'
import { exportForecastShortTerm, exportForecastLongTerm } from '../utils/exportCSV'
import { useAuth } from '../context/AuthContext'

function ForecastingPage() {
  const { community } = useOutletContext()
  const { id } = useParams()
  const { canEdit } = useAuth()
  const { t } = useTranslation()

  // Forecast configuration state
  const [forecastMode, setForecastMode] = useState('short_term')
  const [forecastType, setForecastType] = useState('load')
  const [targetDate, setTargetDate] = useState(
    new Date().toISOString().split('T')[0]
  )
  const [selectedDataset, setSelectedDataset] = useState('')          // Long-term dataset selector
  const [selectedShortTermDataset, setSelectedShortTermDataset] = useState('') // Short-term dataset selector
  const [selectedAssets, setSelectedAssets] = useState([]) // For asset-based selection
  const [isRunning, setIsRunning] = useState(false)
  // 'loading' | 'api' | 'mock'
  const [dataSource, setDataSource] = useState('loading')
  // Separate result state per mode to avoid cross-tab contamination
  const [shortTermResult, setShortTermResult] = useState({ has: false, results: {}, selected: null })
  const [longTermResult, setLongTermResult] = useState({ has: false, results: {}, selected: null })
  const [showWeatherUpload, setShowWeatherUpload] = useState(false)
  const [selectedWeatherDataset, setSelectedWeatherDataset] = useState('')
  // Validation state
  const [showValidation, setShowValidation] = useState(false)
  const [validationFile, setValidationFile] = useState(null)
  const [validationResult, setValidationResult] = useState(null)
  const [isValidating, setIsValidating] = useState(false)

  // Convenience aliases for current mode
  const hasResult = forecastMode === 'short_term' ? shortTermResult.has : longTermResult.has
  const forecastResults = forecastMode === 'short_term' ? shortTermResult.results : longTermResult.results
  const selectedResultAsset = forecastMode === 'short_term' ? shortTermResult.selected : longTermResult.selected
  const setSelectedResultAsset = (val) => {
    if (forecastMode === 'short_term') setShortTermResult(prev => ({ ...prev, selected: val }))
    else setLongTermResult(prev => ({ ...prev, selected: val }))
  }

  // Calculate date constraints
  const today = new Date().toISOString().split('T')[0]
  const maxDateShortTerm = new Date(new Date().getTime() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]

  // Available datasets from community
  const communityDatasets = community?.community_datasets || []
  const buildingDatasets = (community?.buildings || []).flatMap(
    b => (b.datasets || []).map(d => ({ ...d, buildingId: b.building_id }))
  )

  // Get available assets for forecasting (asset-based selection)
  const getAvailableAssets = () => {
    const buildings = community?.buildings || []
    const communityAssets = community?.community_assets || []

    if (forecastType === 'load') {
      // For load forecasts: list all buildings
      const buildingAssets = buildings.map(b => ({
        id: b.id,
        name: b.building_id,
        type: 'building',
        category: 'load',
        label: t('forecasting.building_load_label', { building: b.building_id }),
      }))
      // Add community aggregate option
      return [
        { id: 'community_total', name: t('forecasting.entire_community'), type: 'community', category: 'load', label: t('forecasting.entire_community_label') },
        ...buildingAssets,
      ]
    } else {
      // For generation forecasts: list PV and wind assets
      const pvAssets = communityAssets
        .filter(a => ['solar_pv_farm', 'ground_mounted_pv', 'pv'].includes(a.asset_type))
        .map(a => ({
          id: a.id,
          name: a.name || t('forecasting.solar_pv_farm'),
          type: 'community',
          category: 'pv',
          capacity: a.specs?.capacity_kwp || t('common.n_a'),
          label: `${a.name || t('forecasting.solar_pv_farm')} (${a.specs?.capacity_kwp || t('common.n_a')} kWp)`,
        }))
      const windAssets = communityAssets
        .filter(a => a.asset_type === 'wind_turbine')
        .map(a => ({
          id: a.id,
          name: a.name || t('forecasting.wind_turbine'),
          type: 'community',
          category: 'wind',
          capacity: a.specs?.capacity_kw || t('common.n_a'),
          label: `${a.name || t('forecasting.wind_turbine')} (${a.specs?.capacity_kw || t('common.n_a')} kW)`,
        }))
      const buildingPVAssets = buildings.flatMap(b => {
        const rooftopPVs = (b.building_assets || [])
          .filter(a => a.asset_type === 'rooftop_pv')
          .map(a => ({
            id: `${b.id}_${a.asset_type}`,
            name: t('forecasting.rooftop_pv', { building: b.building_id }),
            type: 'building',
            category: 'pv',
            buildingId: b.building_id,
            capacity: a.specs?.capacity_kwp || t('common.n_a'),
            label: `${t('forecasting.rooftop_pv', { building: b.building_id })} (${a.specs?.capacity_kwp || t('common.n_a')} kWp)`,
          }))
        return rooftopPVs
      })
      return [...pvAssets, ...windAssets, ...buildingPVAssets]
    }
  }

  const availableAssets = getAvailableAssets()

  // Filter datasets by forecast type (legacy support)
  const getRelevantDatasets = () => {
    if (forecastType === 'load') {
      return [
        ...communityDatasets.filter(d =>
          d.dataset_type?.includes('load') || d.kind === 'load'
        ),
        ...buildingDatasets.filter(d =>
          d.dataset_type?.includes('load') || d.kind === 'load'
        ),
      ]
    }
    return [
      ...communityDatasets.filter(d =>
        d.dataset_type?.includes('pv') ||
        d.dataset_type?.includes('wind') ||
        d.kind === 'pv' ||
        d.kind === 'wind'
      ),
      ...buildingDatasets.filter(d =>
        d.dataset_type?.includes('pv') ||
        d.kind === 'pv'
      ),
    ]
  }

  const relevantDatasets = getRelevantDatasets()

  // Get weather datasets
  const weatherDatasets = communityDatasets.filter(d =>
    d.dataset_type?.includes('weather') || d.kind === 'weather'
  )

  // Forecast API mutation hooks
  const shortTermMutation = useShortTermForecast()
  const longTermMutation = useLongTermForecast()

  // Derive community parameters for forecast scaling
  const kpis = useMemo(() => communityKPIs(community), [community])
  const dailyKwh = kpis ? kpis.totalAnnualKwh / 365 : 193
  const totalPvKwp = kpis ? kpis.totalPvKwp : 60

  /**
   * Find an asset by ID from community data (community_assets or building_assets).
   * Returns the raw asset object so the caller can read specs.
   */
  const findAsset = (assetId) => {
    // "community_total" is a synthetic aggregate — no single asset record
    if (assetId === 'community_total') return null

    // Check top-level community assets
    const commAsset = (community?.community_assets || []).find(a => a.id === assetId)
    if (commAsset) return commAsset

    // Check building assets (composite ID: "<buildingId>_<asset_type>")
    for (const building of (community?.buildings || [])) {
      // Some asset IDs are plain UUIDs; others are "<building.id>_<asset_type>"
      const bAsset = (building.building_assets || []).find(
        a => a.id === assetId || `${building.id}_${a.asset_type}` === assetId
      )
      if (bAsset) return bAsset
    }
    return null
  }

  // Derive community latitude for PV profile generation
  const communityLatitude = community?.metadata?.latitude || 47

  // Generate forecast fallback data for an asset (uses community-aware parameters)
  const generateMockForecast = (assetId, type) => {
    const hours = Array.from({ length: 24 }, (_, i) => i)

    if (type === 'load') {
      const isWholeComm = assetId === 'community_total'
      const assetDailyKwh = isWholeComm ? dailyKwh : dailyKwh / Math.max(1, (community?.buildings || []).length)
      const load = czechLoadProfile(assetDailyKwh, 42 + assetId.length, community?.metadata?.country)
      const lower = load.map(v => +(v * 0.87).toFixed(2))
      const upper = load.map(v => +(v * 1.13).toFixed(2))

      return {
        hours,
        forecast: load,
        lower,
        upper,
        peak: Math.max(...load),
        total: load.reduce((sum, v) => sum + v, 0),
        avg: load.reduce((sum, v) => sum + v, 0) / 24,
        confidenceLevel: 'LOW',
      }
    } else {
      const asset = availableAssets.find(a => a.id === assetId)
      const assetKwp = asset?.capacity || totalPvKwp
      const isPV = !assetId.includes('wind') && !assetId.includes('Wind')

      if (isPV) {
        const generation = czechPVProfile(assetKwp, getSeason(targetDate), 43 + assetId.length, communityLatitude)
        const lower = generation.map(v => Math.max(0, +(v * 0.88).toFixed(2)))
        const upper = generation.map(v => +(v * 1.12).toFixed(2))

        return {
          hours,
          forecast: generation,
          lower,
          upper,
          peak: Math.max(...generation),
          total: generation.reduce((sum, v) => sum + v, 0),
          avg: generation.reduce((sum, v) => sum + v, 0) / 24,
          confidenceLevel: 'LOW',
        }
      } else {
        // Wind: deterministic variable profile
        const generation = hours.map((_, i) => +(Math.sin(i * 0.7) * 1.5 + 2 + Math.sin(i * 1.3) * 0.8).toFixed(2))
        const lower = generation.map(v => +(v * 0.80).toFixed(2))
        const upper = generation.map(v => +(v * 1.20).toFixed(2))

        return {
          hours,
          forecast: generation,
          lower,
          upper,
          peak: Math.max(...generation),
          total: generation.reduce((sum, v) => sum + v, 0),
          avg: generation.reduce((sum, v) => sum + v, 0) / 24,
          confidenceLevel: 'LOW',
        }
      }
    }
  }

  // Generate monthly forecast fallback (community-aware seasonal multipliers)
  const generateMockMonthlyForecast = (type) => {
    const months = ['Mar 2026', 'Apr 2026', 'May 2026', 'Jun 2026', 'Jul 2026', 'Aug 2026']
    // Seasonal multipliers: Mediterranean vs Central European
    const isMediterranean = communityLatitude < 43
    const loadMultipliers = isMediterranean
      ? [1.02, 0.92, 0.85, 0.88, 0.90, 0.88]   // Cooling-dominated summer
      : [1.10, 0.95, 0.82, 0.78, 0.76, 0.80]    // Heating-dominated winter
    const pvMultipliers = isMediterranean
      ? [0.78, 0.90, 0.98, 1.05, 1.02, 0.98]    // Higher southern irradiance
      : [0.68, 0.82, 0.92, 1.00, 0.98, 0.90]    // Central European
    const monthlyBaseKwh = (kpis ? kpis.totalAnnualKwh : 70500) / 12

    const forecast = months.map((_, i) => {
      if (type === 'load') return Math.round(monthlyBaseKwh * loadMultipliers[i])
      return Math.round((totalPvKwp * 1000 / 12) * pvMultipliers[i])
    })
    const lower = forecast.map(v => Math.round(v * 0.93))
    const upper = forecast.map(v => Math.round(v * 1.07))

    return {
      months,
      forecast,
      lower,
      upper,
      confidenceLevel: 'LOW',
      rmse: 0.35,
      mae: 0.27,
    }
  }

  /**
   * Execute the forecast via the real backend API.
   *
   * Short-term: POST /v1/forecast/short-term
   *   - Sends selected assets with their capacity/load specs
   *   - Transforms the response (keyed by asset ID) into the component state shape
   *   - Falls back to mock data if the API call fails, so the UI always shows something
   *
   * Long-term: POST /v1/forecast/long-term
   *   - Sends assets with annual_load_kwh / annual_generation_kwh
   *   - Uses the first asset's forecast result for the community view
   *   - Falls back to mock monthly data on error
   */
  /**
   * Fetch all values for a dataset from the dataset-service.
   * Returns an empty array if the dataset ID is falsy or the fetch fails.
   * The response shape is: { values: [{time, value, quality_flag}], total_count, ... }
   */
  const fetchDatasetValues = async (datasetId) => {
    if (!datasetId) return []
    try {
      // Fetch up to 10000 rows (covers a full year of hourly data at 8760 points)
      const response = await getValues(datasetId, { limit: 10000 })
      return response?.values || []
    } catch (err) {
      console.warn('Could not fetch dataset values:', err)
      return []
    }
  }

  /**
   * Transform raw dataset values [{time, value}] into the forecast API's
   * historical_data format [{timestamp, value}].
   */
  const toHistoricalData = (rawValues) =>
    rawValues.map(v => ({
      timestamp: v.time,
      value: v.value,
    }))

  /**
   * Aggregate hourly dataset values into monthly totals for the long-term
   * forecast API's historical_monthly field.
   * Output: [{month: "2025-01", value: 12345.6}, ...]
   */
  const toHistoricalMonthly = (rawValues) => {
    const monthlyMap = {}
    for (const v of rawValues) {
      const month = (v.time || '').substring(0, 7) // "YYYY-MM"
      if (!month) continue
      if (!monthlyMap[month]) monthlyMap[month] = 0
      monthlyMap[month] += v.value
    }
    return Object.entries(monthlyMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, value]) => ({ month, value: Math.round(value * 10) / 10 }))
  }

  const handleRunForecast = async () => {
    // Short-term requires at least one asset to be selected; long-term does not
    // use the asset checkboxes (it uses the community-level dataset selector instead).
    if (forecastMode === 'short_term' && selectedAssets.length === 0) {
      return
    }
    setIsRunning(true)
    setDataSource('loading')

    try {
      if (forecastMode === 'short_term') {
        // --- Fetch weather data if available (shared across all assets) ---
        const weatherDatasetId = selectedWeatherDataset || (weatherDatasets[0]?.id || null)
        let weather_data = null
        if (weatherDatasetId) {
          try {
            const weatherValues = await fetchDatasetValues(weatherDatasetId)
            if (weatherValues.length > 0) {
              weather_data = {
                irradiance_wm2: weatherValues.map(v => v.value),
                temperature_c: weatherValues.map(v => v.value_extra?.temperature_c ?? 20.0),
              }
              console.info(`Weather data: ${weatherValues.length} points from dataset ${weatherDatasetId}`)
            }
          } catch (err) {
            console.warn('Could not fetch weather data:', err)
          }
        }

        // --- Per-asset forecast: each asset uses its own dataset when available ---
        // This ensures buildings with different load profiles get distinct forecasts.
        const results = {}
        let overallConfidence = 'LOW'

        // Helper: find the best dataset for a specific asset.
        // Returns null for buildings without their own dataset — this ensures
        // the backend uses the synthetic profile with that building's own
        // annual_load_kwh and building_type instead of another building's data.
        const findDatasetForAsset = (assetId) => {
          // If user explicitly selected a dataset, use it for all assets
          if (selectedShortTermDataset) return selectedShortTermDataset
          // For community_total, aggregate: use first available dataset
          if (assetId === 'community_total') {
            return relevantDatasets[0]?.id || null
          }
          // For a building, ONLY use that building's own dataset
          const building = (community?.buildings || []).find(b => b.id === assetId)
          if (building) {
            const kind = forecastType === 'load' ? 'load' : 'pv'
            const ownDataset = (building.datasets || []).find(d =>
              d.kind === kind || d.dataset_type?.includes(kind)
            )
            return ownDataset?.id || null  // null = no data → synthetic fallback
          }
          return null
        }

        // Run forecasts for each selected asset individually so each gets its own data
        const forecastPromises = selectedAssets.map(async (assetId) => {
          const assetMeta = availableAssets.find(a => a.id === assetId)
          const rawAsset = findAsset(assetId)
          // For buildings, look up the building object directly to read metadata
          const building = (community?.buildings || []).find(b => b.id === assetId)
          const buildingMeta = building?.metadata || {}
          // Building assets (e.g. rooftop_pv) may have specs with capacity_kwp
          const buildingAssetSpecs = (building?.building_assets || [])[0]?.specs || {}
          // Compute annual load using shared helper (priority: metadata > floor_area+energy_class > country defaults)
          const estimatedLoad = building ? estimateBuildingAnnualKwh(building, community) : null
          const asset = {
            id: assetId,
            type: assetMeta?.type || 'building',
            name: assetMeta?.name || assetId,
            capacity_kwp: rawAsset?.specs?.capacity_kwp || buildingAssetSpecs.capacity_kwp || assetMeta?.capacity || null,
            annual_load_kwh: estimatedLoad || rawAsset?.specs?.annual_load_kwh || null,
            annual_generation_kwh: buildingMeta.annual_generation_kwh || rawAsset?.specs?.annual_generation_kwh || null,
            building_type: building?.building_type || null,
            floor_area_m2: building?.floor_area_m2 || null,
            energy_class: building?.energy_class || null,
            num_units: buildingMeta?.num_units || null,
          }

          // Fetch this asset's own historical data
          const datasetId = findDatasetForAsset(assetId)
          const rawValues = await fetchDatasetValues(datasetId)
          const historical_data = toHistoricalData(rawValues)

          if (historical_data.length > 0) {
            console.info(`Forecast for ${asset.name}: ${historical_data.length} historical points from dataset ${datasetId}`)
          } else {
            console.info(`Forecast for ${asset.name}: no historical data, using synthetic profile`)
          }

          // Call forecast API directly (not via useMutation) to allow parallel calls
          const response = await forecastAPI.shortTerm({
            community_id: id,
            forecast_type: forecastType,
            horizon_hours: 24,
            target_date: targetDate,
            assets: [asset],
            historical_data,
            ...(weather_data && { weather_data }),
          })

          return { assetId, assetMeta, response }
        })

        const forecastResponses = await Promise.all(forecastPromises)

        // Merge all per-asset results
        for (const { assetId, assetMeta, response } of forecastResponses) {
          const confidence = response.metadata?.confidence_level || 'LOW'
          if (confidence === 'HIGH' || (confidence === 'MEDIUM_HIGH' && overallConfidence !== 'HIGH')) {
            overallConfidence = confidence
          } else if (confidence === 'MEDIUM' && overallConfidence === 'LOW') {
            overallConfidence = confidence
          }

          for (const [respAssetId, forecast] of Object.entries(response.forecasts || {})) {
            results[assetId] = {
              assetName: forecast.name || assetMeta?.name || assetId,
              assetLabel: assetMeta?.label || forecast.name || assetId,
              assetCategory: assetMeta?.category || null,
              data: {
                hours: forecast.hours || Array.from({ length: 24 }, (_, i) => i),
                forecast: forecast.forecast || forecast.values || [],
                lower: forecast.confidence_lower || [],
                upper: forecast.confidence_upper || [],
                peak: forecast.peak ?? 0,
                total: forecast.total ?? 0,
                avg: forecast.avg ?? 0,
                mape: forecast.mape ?? 0,
              },
            }
          }
        }

        // If no API results at all, fall back to mock
        if (Object.keys(results).length === 0) {
          setDataSource('mock')
          selectedAssets.forEach(assetId => {
            const asset = availableAssets.find(a => a.id === assetId)
            results[assetId] = {
              assetName: asset?.name || assetId,
              assetLabel: asset?.label || assetId,
              assetCategory: asset?.category || null,
              data: generateMockForecast(assetId, forecastType),
            }
          })
        }

        setDataSource('api')
        setShortTermResult({ has: true, results, selected: selectedAssets[0], confidenceLevel: overallConfidence })

      } else {
        // --- Fetch historical data for the long-term (monthly aggregation) ---
        // selectedDataset is driven by the "Historical Data Source" dropdown in the
        // long-term setup panel. If none selected, try the first relevant dataset.
        const ltDatasetId = selectedDataset || relevantDatasets[0]?.id || null
        const rawValues = await fetchDatasetValues(ltDatasetId)
        const historical_monthly = toHistoricalMonthly(rawValues)

        if (historical_monthly.length > 0) {
          console.info(
            `Long-term forecast: using ${historical_monthly.length} monthly data points` +
            ` aggregated from dataset ${ltDatasetId}`
          )
        } else {
          console.info('Long-term forecast: no historical data available, using synthetic SARIMA baseline')
        }

        // Long-term forecast — send assets with annual figures for SARIMA scaling
        const assets = selectedAssets.map(assetId => {
          const assetMeta = availableAssets.find(a => a.id === assetId)
          const rawAsset = findAsset(assetId)
          return {
            id: assetId,
            type: assetMeta?.type || 'building',
            name: assetMeta?.name || assetId,
            annual_load_kwh: rawAsset?.specs?.annual_load_kwh || kpis?.totalAnnualKwh || 65000,
            annual_generation_kwh: rawAsset?.specs?.annual_generation_kwh || kpis?.annualPvKwh || 42000,
            capacity_kwp: rawAsset?.specs?.capacity_kwp || null,
          }
        })

        const response = await longTermMutation.mutateAsync({
          community_id: id,
          forecast_type: forecastType,
          horizon_months: 12,
          assets,
          historical_monthly,  // Pass aggregated monthly totals for SARIMA
        })

        // Backend returns: { months, display_months, forecasts: { assetId: { name, values,
        //   months, confidence_upper, confidence_lower, mape, rmse, mae } } }
        const firstAssetId = Object.keys(response.forecasts || {})[0]
        const forecast = firstAssetId ? response.forecasts[firstAssetId] : null

        if (forecast) {
          const confidenceLevel = response.metadata?.confidence_level || 'LOW'
          setDataSource('api')
          setLongTermResult({
            has: true,
            results: {
              monthly: {
                assetName: forecast.name || t('forecasting.monthly_forecast_name'),
                assetLabel: forecast.name || t('forecasting.monthly_forecast_name'),
                data: {
                  months: forecast.months || response.display_months || response.months || [],
                  forecast: forecast.values || [],
                  lower: forecast.confidence_lower || [],
                  upper: forecast.confidence_upper || [],
                  mape: forecast.mape ?? 0,
                  rmse: forecast.rmse ?? 0,
                  mae: forecast.mae ?? 0,
                },
              },
            },
            selected: 'monthly',
            confidenceLevel,
          })
        } else {
          // No forecast returned — fall back to mock monthly data
          setDataSource('mock')
          setLongTermResult({
            has: true,
            results: {
              monthly: {
                assetName: t('forecasting.monthly_forecast_name'),
                assetLabel: t('forecasting.monthly_forecast_name'),
                data: generateMockMonthlyForecast(forecastType),
              },
            },
            selected: 'monthly',
          })
        }
      }

    } catch (error) {
      // API call failed — fall back to mock data so the UI remains usable
      console.error('Forecast API call failed, falling back to mock data:', error)
      setDataSource('mock')

      if (forecastMode === 'short_term') {
        const results = {}
        selectedAssets.forEach(assetId => {
          const asset = availableAssets.find(a => a.id === assetId)
          results[assetId] = {
            assetName: asset?.name || assetId,
            assetLabel: asset?.label || assetId,
            data: generateMockForecast(assetId, forecastType),
          }
        })
        setShortTermResult({ has: true, results, selected: selectedAssets[0] })
      } else {
        setLongTermResult({
          has: true,
          results: {
            monthly: {
              assetName: t('forecasting.monthly_forecast_name'),
              assetLabel: t('forecasting.monthly_forecast_name'),
              data: generateMockMonthlyForecast(forecastType),
            },
          },
          selected: 'monthly',
        })
      }
    } finally {
      setIsRunning(false)
    }
  }

  const [isSaving, setIsSaving] = useState(false)
  const [saveStatus, setSaveStatus] = useState(null)  // null | 'success' | 'error'

  const handleSaveResults = async () => {
    const results = forecastMode === 'short_term' ? shortTermResult : longTermResult
    if (!results.has) return
    // Use the community ID from URL params — never use community.id which may be a mock value
    const communityId = id
    if (!communityId) {
      setSaveStatus('error')
      return
    }
    setIsSaving(true)
    setSaveStatus(null)
    try {
      // Save ALL per-asset forecast results, replacing any existing ones for the same asset+type
      const allEntries = Object.values(results.results)
      const modeLabel = forecastMode === 'short_term' ? 'Short-term' : 'Long-term'

      // First, delete existing forecasts for these assets to avoid duplicates
      try {
        const existing = await resultsAPI.list(communityId)
        const existingForecasts = (Array.isArray(existing) ? existing : []).filter(r =>
          r.result_type === 'forecast' &&
          r.parameters?.forecast_mode === forecastMode &&
          r.parameters?.forecast_type === forecastType
        )
        const assetNames = new Set(allEntries.map(e => e?.assetName))
        for (const old of existingForecasts) {
          if (assetNames.has(old.parameters?.asset)) {
            await resultsAPI.delete(communityId, old.id)
          }
        }
      } catch (e) {
        console.warn('Could not clean up old forecasts:', e.message)
      }

      // Now save the new forecasts (one per asset)
      for (const entry of allEntries) {
        const assetName = entry?.assetName || 'unknown'
        const assetCategory = entry?.assetCategory || null
        await resultsAPI.save(communityId, {
          result_type: 'forecast',
          name: `${modeLabel} ${forecastType} forecast - ${assetName} - ${targetDate}`,
          parameters: {
            forecast_mode: forecastMode,
            forecast_type: forecastType,
            target_date: targetDate,
            asset: assetName,
            asset_category: assetCategory,
          },
          result_data: entry?.data || {},
        })
      }

      console.info(`Saved ${allEntries.length} ${forecastType} forecast(s) for all assets`)
      setSaveStatus('success')
      setTimeout(() => setSaveStatus(null), 3000)
    } catch (error) {
      console.error('Failed to save forecast results:', error)
      setSaveStatus('error')
    } finally {
      setIsSaving(false)
    }
  }

  const handleExportCSV = () => {
    if (forecastMode === 'short_term') {
      const results = shortTermResult
      if (!results.has) return
      const selectedData = results.results[results.selected] || Object.values(results.results)[0]
      if (selectedData?.data) {
        exportForecastShortTerm(selectedData.data, selectedData.assetName, targetDate)
      }
    } else {
      const results = longTermResult
      if (!results.has) return
      const selectedData = results.results['monthly'] || Object.values(results.results)[0]
      if (selectedData?.data) {
        exportForecastLongTerm(selectedData.data, selectedData.assetName)
      }
    }
  }

  return (
    <div>
      {/* Page Header */}
      <div className="tool-page-header">
        <h1 className="tool-page-title">{t('forecasting.page_title')}</h1>
        <p className="tool-page-subtitle">
          {community?.name
            ? t('forecasting.page_subtitle_named', { name: community.name })
            : t('forecasting.page_subtitle_default')}
        </p>
      </div>

      {/* Mode Selector Tabs */}
      <Tab.Container activeKey={forecastMode} onSelect={(k) => setForecastMode(k)}>
        <Nav variant="pills" className="mb-4" style={{ gap: 'var(--space-2)' }}>
          <Nav.Item>
            <Nav.Link
              eventKey="short_term"
              style={forecastMode === 'short_term' ? {
                backgroundColor: 'var(--color-primary)',
                color: 'white',
                fontWeight: 600,
              } : {
                backgroundColor: 'var(--color-neutral-100)',
                color: 'var(--color-neutral-700)',
              }}
            >
              {t('forecasting.tab_short_term')}
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              eventKey="long_term"
              style={forecastMode === 'long_term' ? {
                backgroundColor: 'var(--color-primary)',
                color: 'white',
                fontWeight: 600,
              } : {
                backgroundColor: 'var(--color-neutral-100)',
                color: 'var(--color-neutral-700)',
              }}
            >
              {t('forecasting.tab_long_term')}
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          {/* Short-Term Forecast */}
          <Tab.Pane eventKey="short_term">
            <Row className="g-4">
              <Col lg={5} xl={4}>
                <div className="setup-panel">
                  <h5 className="setup-panel-title">
                    {t('forecasting.short_setup_title')}
                  </h5>

                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>
                      {t('forecasting.forecast_type')}
                    </Form.Label>
                    <Form.Select
                      value={forecastType}
                      onChange={(e) => setForecastType(e.target.value)}
                    >
                      <option value="load">{t('forecasting.type_load')}</option>
                      <option value="generation">{t('forecasting.type_generation')}</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>
                      {t('forecasting.target_date')}
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={targetDate}
                      onChange={(e) => setTargetDate(e.target.value)}
                      min={today}
                      max={maxDateShortTerm}
                    />
                    <Form.Text className="text-muted">
                      {t('forecasting.target_date_help')}
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>
                      {forecastType === 'load' ? t('forecasting.buildings_to_forecast') : t('forecasting.assets_to_forecast')}
                    </Form.Label>
                    <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--color-neutral-300)', borderRadius: 'var(--border-radius)', padding: 'var(--space-2)' }}>
                      {availableAssets.length === 0 ? (
                        <div style={{ color: 'var(--color-warning)', fontSize: 'var(--font-size-sm)' }}>
                          {forecastType === 'load' ? t('forecasting.no_buildings_found') : t('forecasting.no_assets_found')}
                        </div>
                      ) : (
                        availableAssets.map((asset) => (
                          <Form.Check
                            key={asset.id}
                            type="checkbox"
                            id={`asset-${asset.id}`}
                            label={asset.label}
                            checked={selectedAssets.includes(asset.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedAssets([...selectedAssets, asset.id])
                              } else {
                                setSelectedAssets(selectedAssets.filter(id => id !== asset.id))
                              }
                            }}
                            className="mb-2"
                          />
                        ))
                      )}
                    </div>
                    <Form.Text className="text-muted">
                      {forecastType === 'load' ? t('forecasting.select_buildings_help') : t('forecasting.select_assets_help')}
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>
                      {t('forecasting.historical_source_optional')}
                    </Form.Label>
                    <Form.Select
                      value={selectedShortTermDataset}
                      onChange={(e) => setSelectedShortTermDataset(e.target.value)}
                    >
                      <option value="">
                        {relevantDatasets.length > 0
                          ? t('forecasting.auto_detect', { name: relevantDatasets[0]?.name || t('forecasting.first_matching_dataset') })
                          : t('forecasting.no_datasets_available')}
                      </option>
                      {relevantDatasets.map((ds) => (
                        <option key={ds.id} value={ds.id}>
                          {ds.name} ({ds.dataset_type || ds.kind})
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      {relevantDatasets.length > 0
                        ? t('forecasting.uploaded_values_help')
                        : t('forecasting.upload_data_help')}
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>
                      {t('forecasting.weather_optional')}
                    </Form.Label>
                    <Form.Select
                      value={selectedWeatherDataset}
                      onChange={(e) => setSelectedWeatherDataset(e.target.value)}
                    >
                      <option value="">{t('forecasting.no_weather_data')}</option>
                      {weatherDatasets.map((ds) => (
                        <option key={ds.id} value={ds.id}>
                          {ds.name} ({ds.dataset_type || ds.kind})
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      {t('forecasting.weather_accuracy_help')}
                    </Form.Text>
                  </Form.Group>

                  {weatherDatasets.length === 0 && (
                    <Alert variant="warning" className="mb-3" style={{ fontSize: 'var(--font-size-xs)' }}>
                      <strong>{t('forecasting.no_weather_alert_title')}</strong> {t('forecasting.no_weather_alert_text')}
                    </Alert>
                  )}

                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => setShowWeatherUpload(!showWeatherUpload)}
                    className="mb-3"
                    style={{ width: '100%' }}
                  >
                    {showWeatherUpload ? t('forecasting.hide_upload_weather') : t('forecasting.show_upload_weather')}
                  </Button>

                  {showWeatherUpload && <WeatherUploadForm onClose={() => setShowWeatherUpload(false)} />}

                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>
                      {t('forecasting.resolution')}
                    </Form.Label>
                    <Form.Select defaultValue="hourly" disabled>
                      <option value="hourly">{t('forecasting.res_hourly')}</option>
                      <option value="15min">{t('forecasting.res_15min')}</option>
                    </Form.Select>
                    <Form.Text className="text-muted">
                      {t('forecasting.res_hourly_help')}
                    </Form.Text>
                  </Form.Group>

                  <hr />

                  <h6 style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-neutral-700)', marginBottom: 'var(--space-3)' }}>
                    {t('forecasting.advanced_params')}
                  </h6>

                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>
                      {t('forecasting.confidence_interval')}
                    </Form.Label>
                    <Form.Select defaultValue="95">
                      <option value="90">90%</option>
                      <option value="95">95%</option>
                      <option value="99">99%</option>
                    </Form.Select>
                  </Form.Group>

                  <Button
                    onClick={handleRunForecast}
                    disabled={isRunning || selectedAssets.length === 0}
                    style={{
                      backgroundColor: 'var(--color-primary)',
                      borderColor: 'var(--color-primary)',
                      fontWeight: 600,
                      width: '100%',
                    }}
                  >
                    {isRunning ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        {t('forecasting.running_forecast')}
                      </>
                    ) : (
                      selectedAssets.length === 1
                        ? t('forecasting.run_forecast_one')
                        : t('forecasting.run_forecast_other', { count: selectedAssets.length })
                    )}
                  </Button>
                  {selectedAssets.length === 0 && (
                    <small className="text-muted d-block mt-2">
                      {t('forecasting.select_at_least_one')}
                    </small>
                  )}
                </div>
              </Col>

              {/* Results Area */}
              <Col lg={7} xl={8}>
                {!hasResult && !isRunning && (
                  <div className="results-panel">
                    <div className="chart-placeholder" style={{ minHeight: '400px' }}>
                      <div className="chart-placeholder-icon">&#x1F4C8;</div>
                      <div className="chart-placeholder-text">
                        {t('forecasting.results_placeholder_title')}
                      </div>
                      <div className="chart-placeholder-subtext">
                        {t('forecasting.results_placeholder_text')}
                      </div>
                    </div>
                  </div>
                )}

                {isRunning && (
                  <div className="results-panel text-center" style={{ padding: 'var(--space-16) var(--space-8)' }}>
                    <Spinner animation="border" style={{ color: 'var(--color-primary)', width: '3rem', height: '3rem' }} />
                    <p className="mt-3" style={{ color: 'var(--color-neutral-600)' }}>
                      {t('forecasting.generating_forecast')}
                    </p>
                  </div>
                )}

                {shortTermResult.has && shortTermResult.results[shortTermResult.selected] && (
                  <div className="results-panel">
                    <div className="results-panel-title">
                      <span>{t('forecasting.forecast_results')}</span>
                      <Badge bg="none" style={{ backgroundColor: '#D1F2EB', color: 'var(--color-success)' }}>
                        {t('forecasting.status_complete')}
                      </Badge>
                    </div>

                    {dataSource === 'mock' && (
                      <div className="alert alert-warning d-flex align-items-center mb-3" role="alert">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        <div>
                          <strong>{t('forecasting.sample_data_label')}</strong> {t('forecasting.sample_data_text')}
                        </div>
                      </div>
                    )}

                    {/* Asset Selector Dropdown (if multiple assets) */}
                    {selectedAssets.length > 1 && (
                      <div className="mb-3">
                        <Form.Label style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>
                          {t('forecasting.viewing_results_for')}
                        </Form.Label>
                        <Form.Select
                          value={shortTermResult.selected}
                          onChange={(e) => setShortTermResult(prev => ({ ...prev, selected: e.target.value }))}
                          style={{
                            maxWidth: '400px',
                            fontSize: 'var(--font-size-sm)',
                            borderColor: 'var(--color-neutral-300)',
                          }}
                        >
                          {selectedAssets.map(assetId => (
                            <option key={assetId} value={assetId}>
                              {shortTermResult.results[assetId]?.assetLabel || assetId}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                    )}

                    {(() => {
                      const result = shortTermResult.results[shortTermResult.selected]
                      const chartData = result?.data

                      if (!chartData) return null

                      // ApexCharts configuration for forecast with confidence interval
                      const chartOptions = {
                        chart: {
                          type: 'line',
                          height: 350,
                          toolbar: { show: true },
                          animations: { enabled: true, speed: 800 },
                        },
                        colors: ['#8CB808', '#E8F4D9', '#E8F4D9'],
                        stroke: {
                          curve: 'smooth',
                          width: [3, 0, 0],
                        },
                        fill: {
                          type: ['solid', 'solid', 'solid'],
                          opacity: [1, 0.3, 0.3],
                        },
                        dataLabels: { enabled: false },
                        legend: {
                          position: 'top',
                          horizontalAlign: 'left',
                          fontSize: '12px',
                        },
                        xaxis: {
                          categories: chartData.hours,
                          title: { text: t('forecasting.axis_hour_of_day'), style: { fontSize: '12px', fontWeight: 500 } },
                          labels: { style: { fontSize: '11px' } },
                        },
                        yaxis: {
                          title: {
                            text: forecastType === 'load' ? t('forecasting.axis_load_kw') : t('forecasting.axis_generation_kw'),
                            style: { fontSize: '12px', fontWeight: 500 },
                          },
                          labels: {
                            style: { fontSize: '11px' },
                            formatter: (value) => value != null ? value.toFixed(1) : '',
                          },
                        },
                        tooltip: {
                          shared: true,
                          intersect: false,
                          y: { formatter: (value) => value != null ? `${value.toFixed(2)} kW` : '' },
                        },
                        grid: {
                          borderColor: '#e0e0e0',
                          strokeDashArray: 3,
                        },
                      }

                      const series = [
                        { name: t('forecasting.series_forecast'), data: chartData.forecast, type: 'line' },
                        { name: t('forecasting.series_upper'), data: chartData.upper, type: 'area' },
                        { name: t('forecasting.series_lower'), data: chartData.lower, type: 'area' },
                      ]

                      return (
                        <>
                          {/* Chart Title */}
                          <h6 style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, marginBottom: 'var(--space-3)', color: 'var(--color-neutral-700)' }}>
                            {t('forecasting.chart_title', {
                              asset: result.assetName,
                              type: forecastType === 'load' ? t('forecasting.chart_load') : t('forecasting.chart_generation'),
                              date: targetDate,
                            })}
                          </h6>

                          {/* Forecast Chart */}
                          <Chart options={chartOptions} series={series} type="line" height={350} />

                          {/* Summary Statistics */}
                          <Row className="g-3 my-3">
                            <Col sm={3}>
                              <Card className="text-center" style={{ border: '1px solid var(--color-neutral-200)' }}>
                                <Card.Body className="py-2">
                                  <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--color-primary)' }}>
                                    {(chartData.peak ?? 0).toFixed(1)}
                                  </div>
                                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>
                                    {t('forecasting.kpi_peak')}
                                  </div>
                                </Card.Body>
                              </Card>
                            </Col>
                            <Col sm={3}>
                              <Card className="text-center" style={{ border: '1px solid var(--color-neutral-200)' }}>
                                <Card.Body className="py-2">
                                  <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--color-primary)' }}>
                                    {(chartData.total ?? 0).toFixed(1)}
                                  </div>
                                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>
                                    {t('forecasting.kpi_total')}
                                  </div>
                                </Card.Body>
                              </Card>
                            </Col>
                            <Col sm={3}>
                              <Card className="text-center" style={{ border: '1px solid var(--color-neutral-200)' }}>
                                <Card.Body className="py-2">
                                  <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--color-primary)' }}>
                                    {(chartData.avg ?? 0).toFixed(1)}
                                  </div>
                                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>
                                    {t('forecasting.kpi_avg')}
                                  </div>
                                </Card.Body>
                              </Card>
                            </Col>
                            <Col sm={3}>
                              <Card
                                className="text-center"
                                style={{
                                  border: '1px solid var(--color-neutral-200)',
                                  backgroundColor: (shortTermResult.confidenceLevel === 'HIGH' || shortTermResult.confidenceLevel === 'MEDIUM_HIGH') ? '#D1F2EB'
                                    : shortTermResult.confidenceLevel === 'MEDIUM' ? '#FFF3CD' : '#F8D7DA',
                                }}
                              >
                                <Card.Body className="py-2">
                                  <div
                                    style={{
                                      fontSize: 'var(--font-size-lg)',
                                      fontWeight: 700,
                                      color: (shortTermResult.confidenceLevel === 'HIGH' || shortTermResult.confidenceLevel === 'MEDIUM_HIGH') ? 'var(--color-success)'
                                        : shortTermResult.confidenceLevel === 'MEDIUM' ? 'var(--color-warning)' : 'var(--color-danger)',
                                    }}
                                  >
                                    {shortTermResult.confidenceLevel || 'LOW'}
                                  </div>
                                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>
                                    {t('forecasting.kpi_confidence')}
                                  </div>
                                </Card.Body>
                              </Card>
                            </Col>
                          </Row>

                          {saveStatus === 'success' && (
                            <Alert variant="success" className="mt-2 py-2" style={{ fontSize: 'var(--font-size-sm)' }}>
                              {t('forecasting.save_success')}
                            </Alert>
                          )}
                          {saveStatus === 'error' && (
                            <Alert variant="danger" className="mt-2 py-2" style={{ fontSize: 'var(--font-size-sm)' }}>
                              {t('forecasting.save_failure')}
                            </Alert>
                          )}
                          <div className="d-flex gap-2">
                            {canEdit && (
                            <Button
                              size="sm"
                              variant="success"
                              onClick={handleSaveResults}
                              disabled={isSaving}
                            >
                              {isSaving ? (
                                <><Spinner animation="border" size="sm" className="me-1" />{t('forecasting.btn_saving')}</>
                              ) : t('forecasting.btn_save_results')}
                            </Button>
                            )}
                            <Button size="sm" variant="outline-secondary" onClick={handleExportCSV}>
                              {t('forecasting.btn_export_csv')}
                            </Button>
                          </div>

                          {/* Validate Against Real Data */}
                          <hr className="my-4" />
                          <div className="mb-3">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => setShowValidation(!showValidation)}
                              style={{ width: '100%' }}
                            >
                              {showValidation ? t('forecasting.btn_hide_validation') : t('forecasting.btn_show_validation')}
                            </Button>
                          </div>
                          {showValidation && (
                            <Card style={{ border: '1px solid var(--color-primary-light)', borderRadius: 'var(--radius-lg)', backgroundColor: 'var(--color-primary-pale)' }}>
                              <Card.Body>
                                <h6 style={{ fontWeight: 600, color: 'var(--color-primary-dark)', marginBottom: 'var(--space-3)' }}>
                                  {t('forecasting.validate_title')}
                                </h6>
                                <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)', marginBottom: 'var(--space-3)' }}>
                                  {t('forecasting.validate_intro')}
                                </p>
                                <Form.Group className="mb-3">
                                  <Form.Label style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>
                                    {t('forecasting.validate_file_label')}
                                  </Form.Label>
                                  <Form.Control
                                    type="file"
                                    accept=".csv"
                                    onChange={(e) => setValidationFile(e.target.files[0])}
                                    size="sm"
                                  />
                                  <Form.Text className="text-muted">
                                    {t('forecasting.validate_file_help')}
                                  </Form.Text>
                                </Form.Group>
                                <Button
                                  size="sm"
                                  variant="primary"
                                  disabled={!validationFile || isValidating}
                                  onClick={async () => {
                                    if (!validationFile || !chartData) return
                                    setIsValidating(true)
                                    setValidationResult(null)
                                    try {
                                      const text = await validationFile.text()
                                      const lines = text.trim().split('\n')
                                      const header = lines[0].toLowerCase()
                                      const rows = lines.slice(1).map(line => {
                                        const parts = line.split(',')
                                        return { timestamp: parts[0]?.trim(), value: parseFloat(parts[1]) }
                                      }).filter(r => r.timestamp && !isNaN(r.value))

                                      const forecastTimestamps = chartData.hours.map((h) => {
                                        const d = new Date(targetDate)
                                        d.setHours(h, 0, 0, 0)
                                        return d.toISOString().replace('.000Z', 'Z')
                                      })

                                      const { data } = await forecastAPI.validate({
                                        community_id: id,
                                        forecast_type: forecastType,
                                        forecast_values: chartData.forecast,
                                        forecast_timestamps: forecastTimestamps,
                                        actual_values: rows.map(r => r.value),
                                        actual_timestamps: rows.map(r => r.timestamp),
                                        dataset_id: selectedShortTermDataset || relevantDatasets[0]?.id || null,
                                      })
                                      setValidationResult(data || {})
                                    } catch (err) {
                                      console.error('Validation failed:', err)
                                      setValidationResult({ error: err.message || 'Validation failed' })
                                    } finally {
                                      setIsValidating(false)
                                    }
                                  }}
                                  style={{ backgroundColor: 'var(--color-primary)', borderColor: 'var(--color-primary)' }}
                                >
                                  {isValidating ? (
                                    <><Spinner animation="border" size="sm" className="me-1" />{t('forecasting.btn_validating')}</>
                                  ) : t('forecasting.btn_run_validation')}
                                </Button>

                                {validationResult && !validationResult.error && (
                                  <div className="mt-4">
                                    <Row className="g-3 mb-3">
                                      <Col sm={3}>
                                        <Card className="text-center" style={{ border: '1px solid var(--color-neutral-200)' }}>
                                          <Card.Body className="py-2">
                                            <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: validationResult.mape < 10 ? 'var(--color-success)' : 'var(--color-warning)' }}>
                                              {(validationResult.mape ?? 0).toFixed(1)}%
                                            </div>
                                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>{t('forecasting.metric_mape')}</div>
                                          </Card.Body>
                                        </Card>
                                      </Col>
                                      <Col sm={3}>
                                        <Card className="text-center" style={{ border: '1px solid var(--color-neutral-200)' }}>
                                          <Card.Body className="py-2">
                                            <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--color-primary)' }}>
                                              {(validationResult.mae ?? 0).toFixed(2)}
                                            </div>
                                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>{t('forecasting.metric_mae')}</div>
                                          </Card.Body>
                                        </Card>
                                      </Col>
                                      <Col sm={3}>
                                        <Card className="text-center" style={{ border: '1px solid var(--color-neutral-200)' }}>
                                          <Card.Body className="py-2">
                                            <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--color-primary)' }}>
                                              {(validationResult.rmse ?? 0).toFixed(2)}
                                            </div>
                                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>{t('forecasting.metric_rmse')}</div>
                                          </Card.Body>
                                        </Card>
                                      </Col>
                                      <Col sm={3}>
                                        <Card className="text-center" style={{ border: '1px solid var(--color-neutral-200)' }}>
                                          <Card.Body className="py-2">
                                            <div style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--color-primary)' }}>
                                              {validationResult.n_matched_points ?? 0}
                                            </div>
                                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>{t('forecasting.metric_matched')}</div>
                                          </Card.Body>
                                        </Card>
                                      </Col>
                                    </Row>

                                    {validationResult.forecast_values?.length > 0 && (
                                      <Chart
                                        options={{
                                          chart: { type: 'line', height: 250, toolbar: { show: false } },
                                          colors: ['#8CB808', '#E74C3C'],
                                          stroke: { curve: 'smooth', width: 2 },
                                          legend: { position: 'top' },
                                          xaxis: {
                                            categories: validationResult.timestamps?.map(t => {
                                              const d = new Date(t)
                                              return `${d.getHours()}:00`
                                            }) || [],
                                            title: { text: 'Hour' },
                                          },
                                          yaxis: {
                                            title: { text: 'kW' },
                                            labels: { formatter: v => v != null ? v.toFixed(1) : '' },
                                          },
                                          tooltip: { y: { formatter: v => v != null ? `${v.toFixed(2)} kW` : '' } },
                                        }}
                                        series={[
                                          { name: t('forecasting.series_forecast'), data: validationResult.forecast_values },
                                          { name: t('forecasting.series_actual'), data: validationResult.actual_values },
                                        ]}
                                        type="line"
                                        height={250}
                                      />
                                    )}

                                    {validationResult.data_appended && (
                                      <Alert variant="success" className="mt-3" style={{ fontSize: 'var(--font-size-xs)' }}>
                                        {t('forecasting.data_appended_success')}
                                      </Alert>
                                    )}
                                  </div>
                                )}

                                {validationResult?.error && (
                                  <Alert variant="danger" className="mt-3" style={{ fontSize: 'var(--font-size-xs)' }}>
                                    {t('forecasting.validation_failed', { error: validationResult.error })}
                                  </Alert>
                                )}
                              </Card.Body>
                            </Card>
                          )}
                        </>
                      )
                    })()}
                  </div>
                )}
              </Col>
            </Row>
          </Tab.Pane>

          {/* Long-Term Forecast */}
          <Tab.Pane eventKey="long_term">
            <Row className="g-4">
              <Col lg={5} xl={4}>
                <div className="setup-panel">
                  <h5 className="setup-panel-title">
                    {t('forecasting.long_setup_title')}
                  </h5>

                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>
                      {t('forecasting.forecast_type')}
                    </Form.Label>
                    <Form.Select
                      value={forecastType}
                      onChange={(e) => setForecastType(e.target.value)}
                    >
                      <option value="load">{t('forecasting.type_load')}</option>
                      <option value="generation">{t('forecasting.type_generation')}</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>
                      {t('forecasting.forecast_horizon')}
                    </Form.Label>
                    <Form.Select defaultValue="6">
                      <option value="3">{t('forecasting.horizon_3m')}</option>
                      <option value="6">{t('forecasting.horizon_6m')}</option>
                      <option value="12">{t('forecasting.horizon_12m')}</option>
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)' }}>
                      {t('forecasting.historical_source')}
                    </Form.Label>
                    <Form.Select
                      value={selectedDataset}
                      onChange={(e) => setSelectedDataset(e.target.value)}
                    >
                      <option value="">
                        {relevantDatasets.length > 0
                          ? t('forecasting.auto_detect', { name: relevantDatasets[0]?.name || t('forecasting.first_matching_dataset') })
                          : t('forecasting.no_datasets_sarima')}
                      </option>
                      {relevantDatasets.map((ds) => (
                        <option key={ds.id} value={ds.id}>
                          {ds.name} ({ds.dataset_type || ds.kind})
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      {relevantDatasets.length > 0
                        ? t('forecasting.sarima_aggregates_help')
                        : t('forecasting.sarima_upload_help')}
                    </Form.Text>
                  </Form.Group>

                  <Alert variant="info" style={{ fontSize: 'var(--font-size-xs)' }}>
                    <strong>{t('forecasting.sarima_note_label')}</strong> {t('forecasting.sarima_note_text')}
                  </Alert>

                  <Button
                    onClick={handleRunForecast}
                    disabled={isRunning}
                    style={{
                      backgroundColor: 'var(--color-primary)',
                      borderColor: 'var(--color-primary)',
                      fontWeight: 600,
                      width: '100%',
                    }}
                  >
                    {isRunning ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        {t('forecasting.running_forecast')}
                      </>
                    ) : (
                      t('forecasting.btn_run_monthly')
                    )}
                  </Button>
                </div>
              </Col>

              {/* Long-term Results */}
              <Col lg={7} xl={8}>
                {!hasResult && !isRunning && (
                  <div className="results-panel">
                    <div className="chart-placeholder" style={{ minHeight: '400px' }}>
                      <div className="chart-placeholder-icon">&#x1F4C5;</div>
                      <div className="chart-placeholder-text">
                        {t('forecasting.monthly_placeholder_title')}
                      </div>
                      <div className="chart-placeholder-subtext">
                        {t('forecasting.monthly_placeholder_text')}
                      </div>
                    </div>
                  </div>
                )}

                {isRunning && (
                  <div className="results-panel text-center" style={{ padding: 'var(--space-16) var(--space-8)' }}>
                    <Spinner animation="border" style={{ color: 'var(--color-primary)', width: '3rem', height: '3rem' }} />
                    <p className="mt-3" style={{ color: 'var(--color-neutral-600)' }}>
                      {t('forecasting.training_sarima')}
                    </p>
                  </div>
                )}

                {longTermResult.has && longTermResult.results[longTermResult.selected] && (
                  <div className="results-panel">
                    <div className="results-panel-title">
                      <span>{t('forecasting.monthly_forecast_results')}</span>
                      <Badge bg="none" style={{ backgroundColor: '#D1F2EB', color: 'var(--color-success)' }}>
                        {t('forecasting.status_complete')}
                      </Badge>
                    </div>

                    {dataSource === 'mock' && (
                      <div className="alert alert-warning d-flex align-items-center mb-3" role="alert">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        <div>
                          <strong>{t('forecasting.sample_data_label')}</strong> {t('forecasting.sample_data_text')}
                        </div>
                      </div>
                    )}

                    {(() => {
                      const result = longTermResult.results[longTermResult.selected]
                      const chartData = result?.data

                      if (!chartData) return null

                      // ApexCharts configuration for monthly forecast
                      const chartOptions = {
                        chart: {
                          type: 'bar',
                          height: 350,
                          toolbar: { show: true },
                        },
                        plotOptions: {
                          bar: {
                            borderRadius: 4,
                            dataLabels: { position: 'top' },
                          },
                        },
                        colors: ['#8CB808'],
                        dataLabels: {
                          enabled: true,
                          formatter: (val) => val != null ? val.toFixed(0) : '',
                          offsetY: -20,
                          style: { fontSize: '11px', colors: ['#304758'] },
                        },
                        xaxis: {
                          categories: chartData.months,
                          title: { text: t('forecasting.axis_month'), style: { fontSize: '12px', fontWeight: 500 } },
                        },
                        yaxis: {
                          title: {
                            text: forecastType === 'load' ? t('forecasting.axis_consumption_kwh') : t('forecasting.axis_generation_kwh'),
                            style: { fontSize: '12px', fontWeight: 500 },
                          },
                          labels: {
                            formatter: (value) => value != null ? value.toFixed(0) : '',
                          },
                        },
                        tooltip: {
                          y: { formatter: (value) => value != null ? `${value.toFixed(0)} kWh` : '' },
                        },
                        grid: {
                          borderColor: '#e0e0e0',
                          strokeDashArray: 3,
                        },
                      }

                      const series = [{ name: t('forecasting.series_forecast'), data: chartData.forecast }]

                      return (
                        <>
                          {/* KPI Pills */}
                          <div className="d-flex gap-2 mb-4">
                            <Badge
                              pill
                              bg={
                                (longTermResult.confidenceLevel === 'HIGH' || longTermResult.confidenceLevel === 'MEDIUM_HIGH') ? 'success'
                                : longTermResult.confidenceLevel === 'MEDIUM' ? 'warning' : 'danger'
                              }
                              style={{ fontSize: 'var(--font-size-sm)', padding: 'var(--space-2) var(--space-4)' }}
                            >
                              {t('forecasting.confidence_badge', { level: longTermResult.confidenceLevel || 'LOW' })}
                            </Badge>
                            <Badge
                              pill
                              bg="secondary"
                              style={{ fontSize: 'var(--font-size-sm)', padding: 'var(--space-2) var(--space-4)' }}
                            >
                              {t('forecasting.rmse_badge', { value: (chartData.rmse ?? 0).toFixed(2) })}
                            </Badge>
                            <Badge
                              pill
                              bg="secondary"
                              style={{ fontSize: 'var(--font-size-sm)', padding: 'var(--space-2) var(--space-4)' }}
                            >
                              {t('forecasting.mae_badge', { value: (chartData.mae ?? 0).toFixed(2) })}
                            </Badge>
                          </div>

                          {/* Monthly Forecast Chart */}
                          <Chart options={chartOptions} series={series} type="bar" height={350} />

                          {/* Monthly Breakdown Table */}
                          <Card className="my-4" style={{ border: '1px solid var(--color-neutral-200)' }}>
                            <Card.Body className="p-0">
                              <table className="table mb-0" style={{ fontSize: 'var(--font-size-sm)' }}>
                                <thead>
                                  <tr>
                                    <th>{t('forecasting.table_month')}</th>
                                    <th className="text-end">{t('forecasting.table_forecast_kwh')}</th>
                                    <th className="text-end">{t('forecasting.table_lower_bound')}</th>
                                    <th className="text-end">{t('forecasting.table_upper_bound')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {chartData.months.map((month, i) => (
                                    <tr key={month}>
                                      <td>{month}</td>
                                      <td className="text-end">{(chartData.forecast[i] ?? 0).toFixed(0)}</td>
                                      <td className="text-end">{(chartData.lower[i] ?? 0).toFixed(0)}</td>
                                      <td className="text-end">{(chartData.upper[i] ?? 0).toFixed(0)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </Card.Body>
                          </Card>

                          {saveStatus === 'success' && (
                            <Alert variant="success" className="mt-2 py-2" style={{ fontSize: 'var(--font-size-sm)' }}>
                              {t('forecasting.save_success')}
                            </Alert>
                          )}
                          {saveStatus === 'error' && (
                            <Alert variant="danger" className="mt-2 py-2" style={{ fontSize: 'var(--font-size-sm)' }}>
                              {t('forecasting.save_failure')}
                            </Alert>
                          )}
                          <div className="d-flex gap-2">
                            {canEdit && (
                            <Button
                              size="sm"
                              variant="success"
                              onClick={handleSaveResults}
                              disabled={isSaving}
                            >
                              {isSaving ? (
                                <><Spinner animation="border" size="sm" className="me-1" />{t('forecasting.btn_saving')}</>
                              ) : t('forecasting.btn_save_results')}
                            </Button>
                            )}
                            <Button size="sm" variant="outline-secondary" onClick={handleExportCSV}>
                              {t('forecasting.btn_export_csv')}
                            </Button>
                          </div>
                        </>
                      )
                    })()}
                  </div>
                )}
              </Col>
            </Row>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  )
}

export default ForecastingPage
